import React, { useEffect, useState } from "react";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { HandHeart, Loader2, BookOpenText, CheckCircle2 } from "lucide-react";

export function PrayWithDialog({ request, open, onOpenChange, onPray, praying }) {
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) setDone(false);
  }, [open, request?.id]);

  if (!request) return null;

  const alreadyPrayed = request.prayed_by_me || done;

  const confirm = async () => {
    await onPray();
    setDone(true);
  };

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      testid="pray-with-dialog"
      title={`Pray for ${request.name}`}
      description="Take a moment to actually lift them up — then let them know."
    >
      <div className="space-y-5 pb-2">
        <p className="text-olive leading-relaxed" data-testid="pray-with-context">
          <span className="font-medium">{request.name}</span> is facing {request.context}.
        </p>

        {/* Scripture for the one praying */}
        <div className="rounded-2xl bg-brand-navy text-brand-ivory p-5" data-testid="pray-with-verse">
          <div className="flex items-center gap-2 mb-2">
            <BookOpenText className="w-4 h-4 text-brand-teal" />
            <p className="text-[11px] uppercase tracking-[0.15em] text-gold">Stand on this</p>
          </div>
          <p className="font-serif italic text-lg leading-relaxed">"{request.verse}"</p>
          <p className="text-brand-ivory-muted text-sm mt-2">— {request.verse_ref}</p>
        </div>

        {/* Example prayer */}
        <div className="rounded-2xl bg-sage/60 p-5" data-testid="pray-with-prayer">
          <div className="flex items-center gap-2 mb-2">
            <HandHeart className="w-4 h-4 text-brand-teal-dark" />
            <p className="text-[11px] uppercase tracking-[0.15em] text-brand-teal-dark">A prayer you can pray</p>
          </div>
          <p className="font-serif text-olive leading-relaxed">{request.prayer}</p>
        </div>

        {alreadyPrayed ? (
          <div className="text-center py-2" data-testid="pray-with-done">
            <div className="rounded-2xl bg-brand-teal/10 w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-6 h-6 text-brand-teal-dark" />
            </div>
            <p className="font-serif text-lg text-olive">Thank you for standing with {request.name}.</p>
            <p className="text-sm text-stone mt-1">They'll see that someone is praying with them.</p>
            <button
              data-testid="pray-with-close"
              onClick={() => onOpenChange(false)}
              className="mt-5 rounded-full border border-black/10 px-6 py-3 text-sm font-medium hover:bg-sage transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <button
            data-testid="pray-confirm-btn"
            onClick={confirm}
            disabled={praying}
            className="w-full flex items-center justify-center gap-2 rounded-xl bg-brand-teal-dark px-5 py-3.5 text-white font-semibold transition-colors duration-200 hover:bg-[#0A6F66] disabled:opacity-60"
          >
            {praying ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandHeart className="w-4 h-4" />}
            I prayed for you
          </button>
        )}
      </div>
    </ResponsiveModal>
  );
}
